import React, { useState, useRef, useEffect } from "react";
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ReactSketchCanvas } from "react-sketch-canvas";

export default function Notizzettel({ titel, notiz = {}, onSpeichern, onSchliessen, storagePfad = "notizzettel" }) {
  const [text, setText] = useState(notiz.text || "");
  const [bilder, setBilder] = useState(notiz.bilder || []);
  const [zeichnung, setZeichnung] = useState(notiz.zeichnung || null);
  const [dokumente, setDokumente] = useState(notiz.dokumente || []);
  const [links, setLinks] = useState(notiz.links || []);
  const [neuerLink, setNeuerLink] = useState({ titel: "", url: "" });
  const [tab, setTab] = useState("text");
  const [uploading, setUploading] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);
  const [hoeren, setHoeren] = useState(false);
  const canvasRef = useRef();
  const bildRef = useRef();
  const dokRef = useRef();
  const erkennungRef = useRef(null);

  // Mikrofon Setup
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const erkennung = new SpeechRecognition();
    erkennung.lang = "de-DE";
    erkennung.continuous = true;
    erkennung.interimResults = false;
    erkennung.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          const transkript = e.results[i][0].transcript.trim();
          setText((prev) => prev + (prev ? " " : "") + transkript);
        }
      }
    };
    erkennung.onend = () => setHoeren(false);
    erkennungRef.current = erkennung;
    return () => erkennung.stop();
  }, []);

  const mikrofonToggle = () => {
    if (!erkennungRef.current) {
      alert("Mikrofon wird von diesem Browser nicht unterstützt.");
      return;
    }
    if (hoeren) {
      erkennungRef.current.stop();
      setHoeren(false);
    } else {
      erkennungRef.current.start();
      setHoeren(true);
    }
  };

  const dateiHochladen = async (datei, ordner) => {
    const pfad = `${storagePfad}/${ordner}/${Date.now()}_${datei.name}`;
    const storageRef = ref(storage, pfad);
    await uploadBytes(storageRef, datei);
    return await getDownloadURL(storageRef);
  };

  const bildHochladen = async (e) => {
    const datei = e.target.files[0];
    if (!datei) return;
    setUploading(true);
    try {
      const url = await dateiHochladen(datei, "bilder");
      setBilder((prev) => [...prev, url]);
    } catch (err) { alert("Fehler: " + err.message); }
    setUploading(false);
  };

  const dokumentHochladen = async (e) => {
    const datei = e.target.files[0];
    if (!datei) return;
    setUploading(true);
    try {
      const url = await dateiHochladen(datei, "dokumente");
      setDokumente((prev) => [...prev, { name: datei.name, url, id: Date.now().toString() }]);
    } catch (err) { alert("Fehler: " + err.message); }
    setUploading(false);
  };

  const linkHinzufuegen = (e) => {
    e.preventDefault();
    if (!neuerLink.url.trim()) return;
    const url = neuerLink.url.startsWith("http") ? neuerLink.url : "https://" + neuerLink.url;
    setLinks((prev) => [...prev, { titel: neuerLink.titel || url, url, id: Date.now().toString() }]);
    setNeuerLink({ titel: "", url: "" });
  };

  const zeichnungLoeschen = () => {
    canvasRef.current?.clearCanvas();
    setZeichnung(null);
  };

  const speichern = async () => {
    let aktuelleZeichnung = zeichnung;
    if (tab === "zeichnung" && canvasRef.current) {
      // Nur exportieren wenn Canvas nicht leer
      const paths = await canvasRef.current.exportPaths();
      if (paths.length > 0 || zeichnung) {
        aktuelleZeichnung = await canvasRef.current.exportImage("png");
      }
    }
    await onSpeichern({ text, bilder, zeichnung: aktuelleZeichnung, dokumente, links });
    setGespeichert(true);
    setTimeout(() => setGespeichert(false), 2000);
  };

  const TABS = [
    { key: "text", label: "📝 Text" },
    { key: "bilder", label: "🖼️ Bilder" },
    { key: "zeichnung", label: "✏️ Zeichnung" },
    { key: "dokumente", label: "📄 Dokumente" },
    { key: "links", label: "🔗 Links" },
  ];

  const mikrofonVerfuegbar = !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onSchliessen()}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <strong>📋 {titel}</strong>
          <button style={styles.iconBtn} onClick={onSchliessen}>✕</button>
        </div>

        <div style={styles.tabs}>
          {TABS.map((t) => (
            <button key={t.key}
              style={{ ...styles.tabBtn, borderBottom: tab === t.key ? "2px solid #1a73e8" : "2px solid transparent", color: tab === t.key ? "#1a73e8" : "#555" }}
              onClick={() => setTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* TEXT */}
        {tab === "text" && (
          <div>
            <div style={styles.textToolbar}>
              {mikrofonVerfuegbar && (
                <button
                  style={{ ...styles.mikrofonBtn, backgroundColor: hoeren ? "#ea4335" : "#f0f2f5", color: hoeren ? "white" : "#333" }}
                  onClick={mikrofonToggle}
                  title={hoeren ? "Aufnahme stoppen" : "Sprache aufnehmen"}
                >
                  {hoeren ? "🔴 Aufnahme läuft... (klicken zum Stoppen)" : "🎙️ Spracheingabe"}
                </button>
              )}
            </div>
            <textarea
              style={styles.textarea}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Notizen, Infos..."
              rows={11}
              autoFocus={!hoeren}
            />
          </div>
        )}

        {/* BILDER */}
        {tab === "bilder" && (
          <div>
            <button style={styles.button} onClick={() => bildRef.current.click()} disabled={uploading}>
              {uploading ? "Lädt hoch..." : "📎 Bild hinzufügen"}
            </button>
            <input ref={bildRef} type="file" accept="image/*" style={{ display: "none" }} onChange={bildHochladen} />
            <div style={styles.bilderGrid}>
              {bilder.map((url, i) => (
                <div key={i} style={{ position: "relative", display: "inline-block" }}>
                  <img src={url} alt="" style={styles.bild} />
                  <button style={styles.bildLoeschen} onClick={() => setBilder(bilder.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              {bilder.length === 0 && <p style={styles.leer}>Noch keine Bilder.</p>}
            </div>
          </div>
        )}

        {/* ZEICHNUNG */}
        {tab === "zeichnung" && (
          <div>
            <div style={{ border: "1px solid #ccc", borderRadius: "4px", overflow: "hidden" }}>
              <ReactSketchCanvas
                ref={canvasRef}
                width="100%"
                height="320px"
                strokeWidth={3}
                strokeColor="#1a3558"
                canvasColor="white"
                backgroundImage={zeichnung || undefined}
                preserveBackgroundImageAspectRatio="none"
              />
            </div>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
              <button style={{ ...styles.button, backgroundColor: "#666" }} onClick={() => canvasRef.current?.undo()}>↩ Rückgängig</button>
              <button style={{ ...styles.button, backgroundColor: "#999" }} onClick={() => canvasRef.current?.clearCanvas()}>🗑️ Striche löschen</button>
              <button style={{ ...styles.button, backgroundColor: "#ea4335" }} onClick={zeichnungLoeschen}>🗑️ Zeichnung komplett löschen</button>
            </div>
            {zeichnung && <p style={styles.hinweis}>💡 Gespeicherte Zeichnung als Hintergrund geladen. Neue Striche werden beim Speichern zusammengeführt.</p>}
          </div>
        )}

        {/* DOKUMENTE */}
        {tab === "dokumente" && (
          <div>
            <button style={styles.button} onClick={() => dokRef.current.click()} disabled={uploading}>
              {uploading ? "Lädt hoch..." : "📎 Dokument hinzufügen"}
            </button>
            <input ref={dokRef} type="file" style={{ display: "none" }} onChange={dokumentHochladen} />
            <div style={{ marginTop: "0.8rem" }}>
              {dokumente.map((d) => (
                <div key={d.id} style={styles.zeile}>
                  <a href={d.url} target="_blank" rel="noreferrer" style={styles.link}>📄 {d.name}</a>
                  <button style={styles.iconBtn} onClick={() => setDokumente(dokumente.filter((x) => x.id !== d.id))}>🗑️</button>
                </div>
              ))}
              {dokumente.length === 0 && <p style={styles.leer}>Keine Dokumente.</p>}
            </div>
          </div>
        )}

        {/* LINKS */}
        {tab === "links" && (
          <div>
            <form onSubmit={linkHinzufuegen} style={styles.linkForm}>
              <input style={styles.input} placeholder="Beschreibung (optional)" value={neuerLink.titel} onChange={(e) => setNeuerLink({ ...neuerLink, titel: e.target.value })} />
              <input style={styles.input} placeholder="URL (z.B. google.de)" value={neuerLink.url} onChange={(e) => setNeuerLink({ ...neuerLink, url: e.target.value })} required />
              <button style={styles.button} type="submit">+</button>
            </form>
            <div>
              {links.map((l) => (
                <div key={l.id} style={styles.zeile}>
                  <a href={l.url} target="_blank" rel="noreferrer" style={styles.link}>🔗 {l.titel}</a>
                  <button style={styles.iconBtn} onClick={() => setLinks(links.filter((x) => x.id !== l.id))}>🗑️</button>
                </div>
              ))}
              {links.length === 0 && <p style={styles.leer}>Keine Links.</p>}
            </div>
          </div>
        )}

        <button
          style={{ ...styles.button, marginTop: "1rem", width: "100%", backgroundColor: gespeichert ? "#34a853" : "#1a73e8" }}
          onClick={speichern}
        >
          {gespeichert ? "✅ Gespeichert!" : "💾 Speichern"}
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" },
  modal: { backgroundColor: "white", borderRadius: "8px", padding: "1.5rem", width: "640px", maxWidth: "95vw", maxHeight: "90vh", overflow: "auto", boxShadow: "0 4px 20px rgba(0,0,0,0.3)" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" },
  tabs: { display: "flex", gap: 0, marginBottom: "1rem", borderBottom: "1px solid #eee", flexWrap: "wrap" },
  tabBtn: { padding: "0.5rem 0.8rem", background: "none", border: "none", cursor: "pointer", fontSize: "0.85rem", fontWeight: "500" },
  textToolbar: { marginBottom: "0.5rem" },
  mikrofonBtn: { padding: "0.4rem 0.8rem", border: "1px solid #ccc", borderRadius: "20px", cursor: "pointer", fontSize: "0.85rem", transition: "all 0.2s" },
  textarea: { width: "100%", padding: "0.6rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px", resize: "vertical", boxSizing: "border-box", fontFamily: "sans-serif" },
  button: { padding: "0.5rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginBottom: "0.3rem" },
  bilderGrid: { display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" },
  bild: { width: "120px", height: "120px", objectFit: "cover", borderRadius: "4px" },
  bildLoeschen: { position: "absolute", top: "4px", right: "4px", background: "rgba(0,0,0,0.6)", color: "white", border: "none", borderRadius: "50%", cursor: "pointer", width: "22px", height: "22px", fontSize: "0.7rem" },
  linkForm: { display: "flex", gap: "0.4rem", marginBottom: "0.8rem", flexWrap: "wrap" },
  input: { flex: 1, padding: "0.5rem", fontSize: "0.9rem", border: "1px solid #ccc", borderRadius: "4px", minWidth: "100px" },
  zeile: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid #f5f5f5" },
  link: { color: "#1a73e8", textDecoration: "none", fontSize: "0.9rem" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "1rem" },
  leer: { color: "#999", fontSize: "0.85rem" },
  hinweis: { fontSize: "0.8rem", color: "#888", marginTop: "0.4rem" },
};
